import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import JsonLd from '@/components/feature/JsonLd';
import { getCategory, listByCategory, hotArticles } from '@/utils/content';
import { site } from '@/mocks/site';
import { useMemo } from 'react';

export default function ColumnPage() {
  const { categoryId = '' } = useParams();
  const category = getCategory(categoryId);
  const [sort, setSort] = useState<'latest' | 'hot'>('latest');

  const list = useMemo(() => {
    const base = listByCategory(categoryId);
    return sort === 'hot'
      ? [...base].sort((a, b) => b.heat - a.heat)
      : [...base].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [categoryId, sort]);

  const sidebarHot = hotArticles(6);

  if (!category) {
    return (
      <PageLayout>
        <Seo title="栏目不存在" description="你访问的栏目不存在或已被移除。" />
        <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-error-warning-line text-5xl text-primary-500" />
          <h1 className="mt-4 text-2xl font-bold text-ink">这个栏目走丢了</h1>
          <p className="mt-2 text-ink-600">换个栏目继续逛吧，精彩内容都在这里。</p>
          <Link to="/" className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-bold text-cream-50 hover:bg-primary-600">
            回到首页
          </Link>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Seo
        title={`${category.name} · ${category.slogan}`}
        description={category.desc}
        keywords="校园爆料,校园风云人物爆料,班长学委秘密合集,校园社团名场面,校园人物反差瞬间"
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${category.name} · 校园爆点`,
          description: category.desc,
          url: typeof window !== 'undefined' ? window.location.href : '',
        }}
      />

      {/* category band */}
      <section className={`bg-gradient-to-r ${category.gradient} relative overflow-hidden`}>
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-10 md:flex-row md:items-center md:justify-between md:px-6 md:py-14">
          <div className="max-w-xl">
            <Breadcrumbs
              items={[
                { label: '首页', to: '/' },
                { label: category.name },
              ]}
            />
            <span className="flex items-center gap-2 font-display text-2xl text-ink md:text-3xl">
              <i className={category.icon} />
              {category.name}
              <span className="text-sm text-ink-600/80 md:text-base">{category.en}</span>
            </span>
            <p className="mt-2 font-display text-lg text-primary-800">{category.slogan}</p>
            <p className="mt-3 text-sm leading-relaxed text-ink-600 md:text-base">{category.desc}</p>
          </div>
          <div className="shrink-0 rounded-2xl bg-cream-50/80 px-6 py-4 text-center backdrop-blur">
            <p className="font-display text-4xl text-primary-600">{list.length}</p>
            <p className="text-xs text-ink-600">篇精选盘点</p>
          </div>
        </div>
      </section>

      {/* list + sidebar */}
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-8 md:px-6 lg:grid-cols-[1fr_320px]">
        <div>
          <div className="mb-5 flex items-center justify-between">
            <h1 className="text-lg font-bold text-ink">全部内容</h1>
            <div className="flex items-center gap-1 rounded-full bg-cream-100 p-1">
              <button
                type="button"
                onClick={() => setSort('latest')}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm ${
                  sort === 'latest' ? 'bg-white font-bold text-primary-700 shadow-soft' : 'text-ink-600'
                }`}
              >
                最新发布
              </button>
              <button
                type="button"
                onClick={() => setSort('hot')}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm ${
                  sort === 'hot' ? 'bg-white font-bold text-primary-700 shadow-soft' : 'text-ink-600'
                }`}
              >
                热度最高
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {list.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-2xl border border-cream-200 bg-white p-5">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-ink">
              <i className="ri-fire-fill text-primary-500" /> 全站热门
            </h2>
            <div className="space-y-3">
              {sidebarHot.map((a) => (
                <ArticleCard key={a.id} article={a} variant="row" showCategory={false} />
              ))}
            </div>
          </div>

          <div className="rounded-2xl bg-primary-500 p-5 text-cream-50">
            <i className="ri-send-plane-line text-3xl" />
            <p className="mt-3 font-bold">也有想分享的名场面？</p>
            <p className="mt-1 text-sm text-cream-100">发给我们，脱敏后帮更多同学快乐吃瓜。</p>
            <a
              href={`mailto:${site.email}`}
              className="mt-4 inline-block whitespace-nowrap rounded-full bg-cream-50 px-5 py-2 text-sm font-bold text-primary-700 hover:scale-105"
            >
              投稿
            </a>
          </div>
        </aside>
      </div>
    </PageLayout>
  );
}